import {
  ConfiguratorResultType,
  ProcessFlowchartEditorConfigurator,
  useProcessFlowchartEditorConfigurator,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {DefaultProcessesPlugin} from 'processes/graph-builder-processes/processes-plugin/DefaultProcessesPlugin';

import {
  FlowchartDataResultType,
  type FlowchartDataResult,
  type LoadedFlowchartData,
  useFlowchartConfigData,
} from './executionFlowchartData';

export interface UseExecutionFlowchartCoreResult {
  /** Base type configurator before per-`editorMode` platform feature overrides. */
  executionEditorConfigurator: ProcessFlowchartEditorConfigurator | null;
  /** True when the flowchart type configurator query failed (distinct from still loading). */
  isConfiguratorLoadError: boolean;
  dataResult: FlowchartDataResult;
  data: LoadedFlowchartData | null;
}

/**
 * Shared data path for execution-style flowchart panes: editor configurator plus loaded template/schemas.
 *
 * `executionEditorConfigurator` is the **base** type configurator (before per-mode platform overrides from
 * {@link buildExecutionEditorConfigurator}). Callers apply that helper per UI mode (viewer vs PCM, etc.).
 *
 * - **ACM:** pass `flowchartConfigId` from the task schema; set `executionFlowchartId` to null.
 * - **PCM / process-backed viewer:** pass `processId`-derived ids — `flowchartConfigId` from the process and
 *   `executionFlowchartId` as the **root process flowchart** id (not the process id; see
 *   `getExecutionFlowchartIdFromProcess`).
 */
export function useExecutionFlowchartCore(args: {
  flowchartConfigId: string | null;
  /** Root flowchart entity id when resolving via process. */
  executionFlowchartId: string | null;
  /** Skips the type configurator query when there is nothing to load (e.g. ACM draft id not yet set). */
  skipFlowchartTypeConfigurator?: boolean;
}): UseExecutionFlowchartCoreResult {
  const configuratorResult = useProcessFlowchartEditorConfigurator(
    DefaultProcessesPlugin.FLOWCHART_TYPE_NAME,
    {skip: args.skipFlowchartTypeConfigurator ?? false}
  );
  const executionEditorConfigurator =
    configuratorResult.resultType === ConfiguratorResultType.SUCCESS ? configuratorResult.configurator : null;
  const isConfiguratorLoadError = configuratorResult.resultType === ConfiguratorResultType.ERROR;

  const dataResult = useFlowchartConfigData(
    args.flowchartConfigId,
    executionEditorConfigurator,
    args.executionFlowchartId
  );
  const data = dataResult.resultType === FlowchartDataResultType.SUCCESS ? dataResult.data : null;

  return {
    isConfiguratorLoadError,
    executionEditorConfigurator,
    dataResult,
    data,
  };
}
